import { actions } from '@/Store';
import { gql, useLazyQuery } from '@apollo/client';
import { AppId } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { isEmpty } from 'ramda';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useAuth from '../useAuth';

const GET_CUSTOMER_PACKAGES = gql(/* GraphQL */ `
  query GetCustomerPackages($appId: AppId!) {
    experience(appId: $appId) {
      customer {
        packages {
          package {
            id
          }
        }
      }
    }
  }
`);

const CUSTOMER_PACKAGE_EVENT_SUBSCRIPTION = gql(/* GraphQL */ `
  subscription customerPackageEventSubscription($appId: AppId!) {
    customerAccount(appId: $appId) {
      account {
        packages {
          package {
            id
          }
        }
      }
    }
  }
`);

const useGetCustomerPackages = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();

  const [fetchCustomerPackages, result] = useLazyQuery(GET_CUSTOMER_PACKAGES, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchCustomerPackages({ variables: { appId: AppId.SkyGoWeb } });
    }
  }, [fetchCustomerPackages, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && result?.data && result?.subscribeToMore) {
      const unsubscribe = result.subscribeToMore({
        document: CUSTOMER_PACKAGE_EVENT_SUBSCRIPTION,
        variables: { appId: AppId.SkyGoWeb },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData?.data) {
            return prev;
          }
          const account = subscriptionData.data?.customerAccount?.account;
          return {
            ...prev,
            experience: {
              ...prev?.experience,
              customer: {
                ...prev?.experience?.customer,
                packages: account?.packages || prev?.experience?.customer?.packages,
              },
            },
          };
        },
      });

      return () => {
        unsubscribe();
      };
    }

    return undefined;
  }, [result, isAuthenticated]);

  const customerPackagesIds = useMemo(() => {
    return (
      result?.data?.experience.customer.packages
        ?.map(pkg => pkg?.package?.id)
        .filter((id): id is string => id !== undefined) || []
    );
  }, [result]);

  useEffect(() => {
    if (!isEmpty(customerPackagesIds)) {
      dispatch(actions.customer.setCustomerPackages(customerPackagesIds));
    }
  }, [customerPackagesIds, dispatch]);
  return { ...result, customerPackagesIds };
};

export default useGetCustomerPackages;
